// The app-level "notes machinery" provider. This is the one piece of the
// notes feature that owns persistence: create/edit/delete/resolve, wired to
// the real `@automatry/persistence`-backed project API when a persisted
// project id is available, and falling back to pure in-memory state
// otherwise (e.g. mid-onboarding, before the project's first save — the
// same behaviour every existing caller already relies on).
//
// This hook deliberately knows NOTHING about how a consumer chooses to
// present notes (a docked panel, a rail, a modal) or about "promotion"
// (turning a note into a requirement) — that's app-specific: onboarding
// promotes into `CreateProjectDraft.requirementTexts` via the Lane-A
// pipeline, and any other surface would promote into whatever its own
// requirements model is. Consumers that DO promote should keep their own
// thin wrapper around `editSavedNote` for the status flip (see
// `OnboardingFlow.tsx`'s `promoteProjectNoteToRequirement`).
//
// INTEGRATOR NOTE (ui-shell): mount this hook once per active project and
// spread `notesContract` straight into `<AssistantDock notes={...} />` —
// that's the whole integration surface. No matter where the dock ends up
// living in the app shell, notes created/edited/deleted/resolved through it
// persist for real.
import { useState } from "react";

import {
  createProjectNote,
  deleteProjectNote,
  resolveProjectNoteConflict,
  updateProjectNote,
  type ProjectDetailResponse,
  type ProjectNoteDismissedConflictPairResponse,
  type ProjectNoteResponse
} from "../../app/projectsClient";
import { useProjectNotes } from "./useProjectNotes";
import type {
  AssistantNotesContract,
  ProjectNote,
  ProjectNoteInput,
  ProjectNotePatch,
  ProjectNoteSubjectRef,
  UseProjectNotesResult
} from "./types";

export type ProjectNotesSaveContext = "save" | "edit" | "delete" | "resolve";

export interface UseProjectNotesControllerOptions {
  /**
   * Stable key for local note-id sequencing/reset. Same contract as the
   * underlying `useProjectNotes({ projectId })` — pass whatever identifies
   * "this notes list" for the caller (a real project id, or a synthetic
   * draft id before one exists).
   */
  projectId: string;
  /**
   * The REAL persisted project id, once one exists. `null`/`undefined`
   * means the project hasn't been created on the server yet — every CRUD
   * action then stays purely local/in-memory until a persisted id is
   * supplied on a later render (at which point new actions start
   * persisting; notes captured before that point are not retroactively
   * pushed — same pre-existing behaviour as onboarding today).
   */
  persistentProjectId?: string | null;
  initialNotes?: readonly ProjectNote[];
  now?: () => string;
  idFactory?: () => string;
  subjectOptions?: readonly ProjectNoteSubjectRef[];
  noteDraftPlaceholder?: string;
  /**
   * Called whenever a persisted CRUD call fails, in addition to the action
   * rejecting (callers still need the rejection so UI can keep an in-flight
   * draft intact, matching `AssistantDock`'s existing catch-and-preserve
   * behaviour) — use this hook to surface a toast/notification.
   */
  onSaveError?: (context: ProjectNotesSaveContext, error: unknown) => void;
}

export interface ProjectNotesController extends UseProjectNotesResult {
  isSaving: boolean;
  saveNote: (note: ProjectNoteInput) => Promise<ProjectNote>;
  editSavedNote: (noteId: string, patch: ProjectNotePatch) => Promise<void>;
  deleteSavedNote: (noteId: string) => Promise<void>;
  resolveSavedConflict: (noteId: string) => Promise<void>;
  /** Ready-made prop bag: spread directly into `<AssistantDock notes={...} />`. */
  notesContract: AssistantNotesContract;
}

export function projectNoteResponseToNote(note: ProjectNoteResponse): ProjectNote {
  return {
    id: note.id,
    kind: note.kind,
    text: note.text,
    authorType: note.authorType,
    status: note.status,
    createdAt: note.createdAt,
    updatedAt: note.updatedAt,
    ...(note.subjectRef ? { subjectRef: { ...note.subjectRef } } : {}),
    ...(note.aiProvenance ? { aiProvenance: { ...note.aiProvenance } } : {}),
    ...(note.conflictsWith.length > 0
      ? { conflictsWith: [...note.conflictsWith] }
      : {}),
    ...((note.dismissedConflictPairs ?? []).length > 0
      ? {
          dismissedConflictPairs: (note.dismissedConflictPairs ?? []).map(
            dismissedConflictPairForApi
          )
        }
      : {}),
    ...(note.promotedRequirementId
      ? { promotedRequirementId: note.promotedRequirementId }
      : {}),
    ...(note.lastEditedBy ? { lastEditedBy: note.lastEditedBy } : {})
  };
}

export function projectNotesFromDetail(detail: ProjectDetailResponse): ProjectNote[] {
  return (detail.notes ?? []).map(projectNoteResponseToNote);
}

function dismissedConflictPairForApi(pair: {
  noteIds: readonly [string, string];
  contentFingerprints: readonly [string, string];
}): ProjectNoteDismissedConflictPairResponse {
  return {
    noteIds: [pair.noteIds[0], pair.noteIds[1]],
    contentFingerprints: [pair.contentFingerprints[0], pair.contentFingerprints[1]]
  };
}

function noteInputForApi(note: ProjectNoteInput) {
  return {
    kind: note.kind,
    text: note.text,
    ...(note.subjectRef ? { subjectRef: note.subjectRef } : {}),
    ...(note.authorType ? { authorType: note.authorType } : {}),
    ...(note.aiProvenance ? { aiProvenance: note.aiProvenance } : {}),
    ...(note.conflictsWith ? { conflictsWith: [...note.conflictsWith] } : {}),
    ...(note.dismissedConflictPairs
      ? {
          dismissedConflictPairs: note.dismissedConflictPairs.map(
            dismissedConflictPairForApi
          )
        }
      : {}),
    ...(note.promotedRequirementId
      ? { promotedRequirementId: note.promotedRequirementId }
      : {})
  };
}

function notePatchForApi(patch: ProjectNotePatch) {
  return {
    ...(patch.kind ? { kind: patch.kind } : {}),
    ...(patch.text !== undefined ? { text: patch.text } : {}),
    ...(patch.subjectRef !== undefined ? { subjectRef: patch.subjectRef } : {}),
    ...(patch.authorType ? { authorType: patch.authorType } : {}),
    ...(patch.status ? { status: patch.status } : {}),
    ...(patch.aiProvenance !== undefined ? { aiProvenance: patch.aiProvenance } : {}),
    ...(patch.conflictsWith ? { conflictsWith: [...patch.conflictsWith] } : {}),
    ...(patch.dismissedConflictPairs
      ? {
          dismissedConflictPairs: patch.dismissedConflictPairs.map(
            dismissedConflictPairForApi
          )
        }
      : {}),
    ...(patch.promotedRequirementId !== undefined
      ? { promotedRequirementId: patch.promotedRequirementId }
      : {}),
    ...(patch.lastEditedBy !== undefined ? { lastEditedBy: patch.lastEditedBy } : {})
  };
}

export function useProjectNotesController(
  options: UseProjectNotesControllerOptions
): ProjectNotesController {
  const base = useProjectNotes({
    projectId: options.projectId,
    ...(options.initialNotes !== undefined
      ? { initialNotes: options.initialNotes }
      : {}),
    ...(options.now ? { now: options.now } : {}),
    ...(options.idFactory ? { idFactory: options.idFactory } : {})
  });
  const [isSaving, setIsSaving] = useState(false);
  const activeProjectId = options.persistentProjectId ?? null;

  const saveNote = async (note: ProjectNoteInput): Promise<ProjectNote> => {
    if (!activeProjectId) {
      return base.addNote(note);
    }

    setIsSaving(true);

    try {
      const saved = await createProjectNote(activeProjectId, noteInputForApi(note));
      const nextNote = projectNoteResponseToNote(saved);

      // `upsertNote` reconciles conflict back-references against whatever
      // this browser already has in memory, so a note this one conflicts
      // with gets flagged locally too — not just on the next full reload.
      base.upsertNote(nextNote);
      return nextNote;
    } catch (error) {
      options.onSaveError?.("save", error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const editSavedNote = async (
    noteId: string,
    patch: ProjectNotePatch
  ): Promise<void> => {
    if (!activeProjectId) {
      base.editNote(noteId, patch);
      return;
    }

    setIsSaving(true);

    try {
      const saved = await updateProjectNote(
        activeProjectId,
        noteId,
        notePatchForApi(patch)
      );

      base.upsertNote(projectNoteResponseToNote(saved));
    } catch (error) {
      options.onSaveError?.("edit", error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const deleteSavedNote = async (noteId: string): Promise<void> => {
    if (!activeProjectId) {
      base.deleteNote(noteId);
      return;
    }

    setIsSaving(true);

    try {
      await deleteProjectNote(activeProjectId, noteId);
      // The server has already scrubbed this note's id out of every other
      // note's `conflictsWith` (sqliteStore/firestoreStore `deleteNote`);
      // mirror that locally so a surviving note doesn't keep showing a
      // conflict against a note that's gone until the next reload.
      base.deleteNote(noteId);
    } catch (error) {
      options.onSaveError?.("delete", error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const resolveSavedConflict = async (noteId: string): Promise<void> => {
    if (!activeProjectId) {
      base.resolveConflict(noteId);
      return;
    }

    setIsSaving(true);

    try {
      await resolveProjectNoteConflict(activeProjectId, noteId);
      // Mirrors the server's bidirectional scrub locally.
      base.resolveConflict(noteId);
    } catch (error) {
      options.onSaveError?.("resolve", error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const notesContract: AssistantNotesContract = {
    notes: base.notes,
    onAddNote: async (note) => {
      await saveNote(note);
    },
    onEditNote: editSavedNote,
    onDeleteNote: deleteSavedNote,
    onResolveConflict: resolveSavedConflict,
    isSaving,
    ...(options.subjectOptions ? { subjectOptions: options.subjectOptions } : {}),
    ...(options.noteDraftPlaceholder
      ? { noteDraftPlaceholder: options.noteDraftPlaceholder }
      : {})
  };

  return {
    ...base,
    isSaving,
    saveNote,
    editSavedNote,
    deleteSavedNote,
    resolveSavedConflict,
    notesContract
  };
}
